import { formatDate, formatDateTime } from '@/lib/date-time';
/**
 * @file routes/_app/settings/recycle-bin.tsx
 * @description 回收站頁面（Task 74）。
 *
 * 使用已存在的 /api/recycle-bin 路由（Task 33）。
 * 功能：
 * - 列表查詢（伺服器端分頁）：類型 / 刪除內容描述 / 刪除當下流程階段 / 刪除者姓名 / 刪除時間（含時分秒）/
 *   預定清除時間 / 狀態。2026-09-15 回報序24：需看得出「誰在什麼時間刪除了哪一個案件的哪個階段」，
 *   保留時長由 30 天改為 3 個月。
 * - 「復原」按鈕：POST /api/recycle-bin/:id/restore（需 recycle-bin.restore）
 * - 「永久刪除」按鈕：DELETE /api/recycle-bin/:id（需 recycle-bin.purge，僅 admin）
 *
 * 視覺：ListPageShell + StatusPill + DataFieldGrid
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, Select, Space, Table, Tooltip, Typography } from 'antd';
import { message, modal } from '@/lib/antd-app';
import type { ColumnsType } from 'antd/es/table';
import { RotateCcw, Trash2, Recycle } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuthStore } from '@/lib/auth-context';
import { ListPageShell, PageMetaItem, StatusPill } from '@/components/shell';
import { color, font, text } from '@/styles/tokens';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/settings/recycle-bin')({ component: RecycleBinPage });

/**
 * 回收站條目實體類型（對應後端 recycleBin 表）。
 */
interface RecycleBinEntry {
  id: number;
  entityType: string;
  entityId: number;
  snapshot: unknown;
  deletedBy: number;
  deletedAt: string;
  scheduledPurgeAt: string;
  restoredAt: string | null;
  restoredBy: number | null;
  /** 刪除者姓名（後端 join users） */
  deletedByName: string | null;
  /** 還原者姓名 */
  restoredByName: string | null;
  /** 實體類型中文名稱（案件 / 客戶 / 檔案 / 表單） */
  entityLabel: string;
  /** 刪除內容描述（如「稽核編號為…，莊宗達，驗證註冊碼為…」案件） */
  description: string;
  /** 刪除當下的流程階段（僅案件） */
  stage: string | null;
}

/** 類型篩選選項 */
const ENTITY_TYPE_OPTIONS = [
  { value: '', label: '全部類型' },
  { value: 'cases', label: '案件' },
  { value: 'clients', label: '客戶' },
  { value: 'files', label: '檔案' },
  { value: 'form-templates', label: '表單' },
];

/** 刪除時間格式：YYYY/MM/DD HH:mm:ss */
function formatDateTimeZh(iso: string): string {
  return formatDateTime(iso);
}

/**
 * 後端分頁響應結構。
 */
interface RecycleBinListResponse {
  items: RecycleBinEntry[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** 回收站頁面 */
function RecycleBinPage() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.roles.includes('admin');

  // 伺服器端分頁 + 類型篩選（後端 pageSize 上限 50）
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [entityType, setEntityType] = useState('');

  /** 查詢回收站條目（僅顯示未還原的記錄） */
  const { data, isLoading } = useQuery({
    queryKey: ['settings-recycle-bin', page, pageSize, entityType],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (entityType) params.set('entityType', entityType);
      return apiClient.get<RecycleBinListResponse>(`/api/recycle-bin?${params.toString()}`);
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  /**
   * 還原 Mutation：POST /api/recycle-bin/:id/restore
   * 需 recycle-bin.restore 能力（admin / manager）。
   */
  const restore = useMutation({
    mutationFn: (id: number) => apiClient.post<{ ok: boolean }>(`/api/recycle-bin/${id}/restore`),
    onSuccess: () => {
      message.success('記錄已還原');
      void qc.invalidateQueries({ queryKey: ['settings-recycle-bin'] });
    },
  });

  /**
   * 永久刪除 Mutation：DELETE /api/recycle-bin/:id
   * 需 recycle-bin.purge 能力（僅 admin）。
   */
  const purge = useMutation({
    mutationFn: (id: number) => apiClient.delete<{ ok: boolean }>(`/api/recycle-bin/${id}`),
    onSuccess: () => {
      message.success('記錄已永久刪除');
      void qc.invalidateQueries({ queryKey: ['settings-recycle-bin'] });
    },
  });

  const columns: ColumnsType<RecycleBinEntry> = [
    {
      title: '類型',
      dataIndex: 'entityLabel',
      width: 90,
      render: (v: string) => (
        <StatusPill tone="neutral" size="sm" variant="outline">
          {v}
        </StatusPill>
      ),
    },
    {
      title: '刪除內容',
      dataIndex: 'description',
      render: (v: string) => <Typography.Text style={{ fontSize: text.sm }}>{v}</Typography.Text>,
    },
    {
      title: '刪除時流程階段',
      dataIndex: 'stage',
      width: 130,
      render: (v: string | null) =>
        v ? (
          <StatusPill tone="info" size="sm">
            {v}
          </StatusPill>
        ) : (
          <span style={{ color: color.text.muted }}>—</span>
        ),
    },
    {
      title: '刪除者',
      dataIndex: 'deletedByName',
      width: 110,
      render: (v: string | null, row) => v ?? `#${row.deletedBy}`,
    },
    {
      title: '刪除時間',
      dataIndex: 'deletedAt',
      width: 190,
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.text.secondary }}>
          {formatDateTimeZh(v)}
        </span>
      ),
    },
    {
      title: '預定清除時間',
      dataIndex: 'scheduledPurgeAt',
      width: 130,
      render: (v: string) => (
        <span style={{ fontFamily: font.mono, fontSize: text.sm, color: color.state.warning }}>
          {formatDate(v)}
        </span>
      ),
    },
    {
      title: '狀態',
      dataIndex: 'restoredAt',
      width: 100,
      render: (v: string | null) =>
        v ? (
          <StatusPill tone="success" size="sm" dot>
            已還原
          </StatusPill>
        ) : (
          <StatusPill tone="warning" size="sm" dot>
            待處理
          </StatusPill>
        ),
    },
    {
      title: '操作',
      key: '_actions',
      width: 180,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4}>
          {/* 復原按鈕（需 recycle-bin.restore） */}
          {!row.restoredAt && (
            <Tooltip title="復原">
              <Button
                size="small"
                icon={<RotateCcw size={12} />}
                onClick={() =>
                  modal.confirm({
                    title: '確定復原此記錄？',
                    okText: '復原',
                    onOk: () => restore.mutate(row.id),
                  })
                }
              >
                復原
              </Button>
            </Tooltip>
          )}

          {/* 永久刪除按鈕（需 recycle-bin.purge，僅 admin） */}
          {isAdmin && (
            <Tooltip title="永久刪除（不可逆）">
              <Button
                size="small"
                danger
                icon={<Trash2 size={12} />}
                onClick={() =>
                  modal.confirm({
                    title: '確定永久刪除？',
                    content: '永久刪除後無法還原，請謹慎操作。',
                    okText: '永久刪除',
                    okButtonProps: { danger: true },
                    onOk: () => purge.mutate(row.id),
                  })
                }
              >
                永久刪除
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <ListPageShell
      title="回收站"
      description="查看誰在什麼時間刪除了哪些記錄（案件含刪除當下的流程階段）；刪除後保留 3 個月，可在預定清除日期前復原或永久刪除。"
      meta={
        <>
          <PageMetaItem icon={<Recycle size={13} />} label="待處理" value={`${total} 筆`} />
          <PageMetaItem label="保留時長" value="3 個月" />
        </>
      }
      filters={
        <Select
          style={{ width: 160 }}
          value={entityType}
          options={ENTITY_TYPE_OPTIONS}
          onChange={(v: string) => {
            setEntityType(v);
            setPage(1);
          }}
        />
      }
    >
      <Table<RecycleBinEntry>
        rowKey="id"
        columns={columns}
        dataSource={items}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50],
          showTotal: (t) => `共 ${t} 筆`,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        scroll={{ x: 1200 }}
      />
    </ListPageShell>
  );
}
